import React from 'react'
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'

interface Deal {
  id: number
  title: string
  amount: string
  customer_name: string
}

interface Scenario {
  id?: number
  deal_id?: number
  scenario_number: number
  bank?: string
  type?: string
  loan_amount?: number
  insured?: boolean
  insurance_rate?: number
  interest_rate?: number
  term?: number
  payment_frequency?: string
  payment?: number
  comments?: string
  created_at?: string
  updated_at?: string
}

interface ScenarioPDFProps {
  deal: Deal
  scenarios: Scenario[]
}

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica'
  },
  header: {
    marginBottom: 20
  },
  title: {
    fontSize: 24,
    marginBottom: 10
  },
  subtitle: {
    fontSize: 14,
    color: '#666'
  },
  scenariosContainer: {
    flexDirection: 'row',
    marginTop: 20
  },
  scenarioColumn: {
    flex: 1,
    marginHorizontal: 10
  },
  scenarioTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10
  },
  field: {
    marginBottom: 10
  },
  label: {
    fontSize: 10,
    color: '#666',
    marginBottom: 2
  },
  value: {
    fontSize: 12
  }
})

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(amount)
}

const ScenarioPDF: React.FC<ScenarioPDFProps> = ({ deal, scenarios }) => {
  // Filter out empty scenarios for PDF
  const validScenarios = scenarios.filter(s => s.loan_amount && s.loan_amount > 0)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Mortgage Scenarios</Text>
          <Text style={styles.subtitle}>
            {deal.title} - {deal.customer_name}
          </Text>
        </View>

        <View style={styles.scenariosContainer}>
          {validScenarios.map(scenario => (
            <View key={scenario.scenario_number} style={styles.scenarioColumn}>
              <Text style={styles.scenarioTitle}>
                Scenario {scenario.scenario_number}
              </Text>

              {scenario.bank && (
                <View style={styles.field}>
                  <Text style={styles.label}>Bank</Text>
                  <Text style={styles.value}>{scenario.bank}</Text>
                </View>
              )}

              {scenario.type && (
                <View style={styles.field}>
                  <Text style={styles.label}>Type</Text>
                  <Text style={styles.value}>{scenario.type}</Text>
                </View>
              )}

              {scenario.loan_amount && (
                <View style={styles.field}>
                  <Text style={styles.label}>Loan Amount</Text>
                  <Text style={styles.value}>{formatCurrency(scenario.loan_amount)}</Text>
                </View>
              )}

              {scenario.insured && (
                <View style={styles.field}>
                  <Text style={styles.label}>Insured</Text>
                  <Text style={styles.value}>Yes{scenario.insurance_rate ? ` (${scenario.insurance_rate}%)` : ''}</Text>
                </View>
              )}

              {scenario.interest_rate && (
                <View style={styles.field}>
                  <Text style={styles.label}>Interest Rate</Text>
                  <Text style={styles.value}>{scenario.interest_rate}%</Text>
                </View>
              )}

              {scenario.term && (
                <View style={styles.field}>
                  <Text style={styles.label}>Term</Text>
                  <Text style={styles.value}>{scenario.term} years</Text>
                </View>
              )}

              {scenario.payment_frequency && (
                <View style={styles.field}>
                  <Text style={styles.label}>Payment Frequency</Text>
                  <Text style={styles.value}>{scenario.payment_frequency}</Text>
                </View>
              )}

              {scenario.payment && scenario.payment > 0 && (
                <View style={styles.field}>
                  <Text style={styles.label}>Calculated Payment</Text>
                  <Text style={styles.value}>{formatCurrency(scenario.payment)}</Text>
                </View>
              )}

              {scenario.comments && (
                <View style={styles.field}>
                  <Text style={styles.label}>Comments</Text>
                  <Text style={styles.value}>{scenario.comments}</Text>
                </View>
              )}
            </View>
          ))}
        </View>
      </Page>
    </Document>
  )
}

export default ScenarioPDF 